<template>
    <LoadingComponent :props="loading" />

    <div id="cookies" class="db-card db-tab-div active" :class="{ 'on-update': onUpdateActive }">
        <div class="db-card-header items-center justify-between gap-4">
            <h3 class="db-card-title">{{ $t("menu.cookies") }}</h3>
        
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="db-btn h-9 py-2 px-3 text-white bg-primary ml-3"
                    @click="refreshFromServer"
                    :disabled="isRefreshingView"
                    :class="{ 'disabled:opacity-50 disabled:bg-gray': isRefreshingView }"
                >
                    <template v-if="isRefreshingView">
                        <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                        </svg>
                    </template>
                    <template v-else>
                        <i class="fa-solid fa-arrows-rotate"></i>
                    </template>
                </button>
            </div></div>
        <div class="db-card-body">
            <form @submit.prevent="save" class="d-block w-full">
                <div class="form-row">
                    <div class="form-col-12 sm:form-col-12">
                        <label for="cookies_details_page_id" class="db-field-title required">
                            {{ $t("label.cookies_details_page") }}
                        </label>
                        <vue-select class="db-field-control f-b-custom-select" id="cookies_details_page_id"
                            v-bind:class="errors.cookies_details_page_id ? 'invalid' : ''"
                            v-model="form.cookies_details_page_id" :options="pages" label-by="title" value-by="id"
                            :closeOnSelect="true" :searchable="true" :clearOnClose="true" placeholder="--"
                            search-placeholder="--" />
                        <small class="db-field-alert" v-if="errors.cookies_details_page_id">
                            {{ errors.cookies_details_page_id[0] }}
                        </small>
                    </div>
                    <div class="form-col-12">
                        <label for="cookies_summary" class="db-field-title required">
                            {{ $t("label.cookies_summary") }}
                        </label>
                        <textarea v-model="form.cookies_summary" v-bind:class="errors.cookies_summary ? 'invalid' : ''"
                            id="cookies_summary" class="db-field-control"></textarea>
                        <small class="db-field-alert" v-if="errors.cookies_summary">
                            {{ errors.cookies_summary[0] }}
                        </small>
                    </div>
                    <div class="form-col-12 border-t pt-5 mt-3">
                        <button type="submit" class="db-btn text-white bg-primary" :disabled="isSaving">
                            <template v-if="isSaving">
                                <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span>{{ $t('button.updating') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab lab-fill-save"></i>
                                <span>{{ $t("button.save") }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import LoadingComponent from "../../components/LoadingComponent";
import alertService from "../../../../services/alertService";
import statusEnum from "../../../../enums/modules/statusEnum";

export default {
    name: "CookiesComponent",
    components: { LoadingComponent },
    data() {
        return {
            isRefreshingView: false,
            loading: {
                isActive: false,
            },
            form: {
                cookies_details_page_id: null,
                cookies_summary: "",
            },
            isSaving: false,
            onUpdateActive: false,
            errors: {},
        };
    },
    computed: {
        pages: function () {
            return this.$store.getters["page/lists"];
        },
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        fetchData: function (showOverlay = true) {
            if (showOverlay) {
                this.loading.isActive = true;
            }
            const pagePromise = this.$store.dispatch("page/lists", { status: statusEnum.ACTIVE });
            const cookiesPromise = this.$store.dispatch("cookies/lists");

            return Promise.all([pagePromise, cookiesPromise])
                .then(([pageRes, cookiesRes]) => {
                    if (cookiesRes && cookiesRes.data && cookiesRes.data.data) {
                        this.form.cookies_details_page_id = cookiesRes.data.data.cookies_details_page_id
                            ? Number(cookiesRes.data.data.cookies_details_page_id)
                            : null;
                        this.form.cookies_summary = cookiesRes.data.data.cookies_summary || "";
                    }
                    if (showOverlay) {
                        this.loading.isActive = false;
                    }
                })
                .catch((err) => {
                    if (showOverlay) {
                        this.loading.isActive = false;
                    }
                });
        },
        refreshFromServer: function () {
            if (this.isRefreshingView) {
                return;
            }
            this.isRefreshingView = true;
            this.onUpdateActive = true;
            this.fetchData(false).then(() => {
                alertService.info(this.$t('button.refresh'));
                this.isRefreshingView = false;
                this.onUpdateActive = false;
            }).catch(() => {
                this.isRefreshingView = false;
                this.onUpdateActive = false;
            });
        },
        save: function () {
            try {
                this.isSaving = true;
                this.onUpdateActive = true;
                this.$store.dispatch("cookies/save", this.form).then((res) => {
                    alertService.successFlip(
                        1,
                        this.$t("menu.cookies")
                    );
                    this.$store.dispatch("cookies/lists").then((response) => {
                        if (response && response.data && response.data.data) {
                            this.form.cookies_details_page_id = response.data.data.cookies_details_page_id
                                ? Number(response.data.data.cookies_details_page_id)
                                : null;
                            this.form.cookies_summary = response.data.data.cookies_summary || "";
                        }
                    }).catch(() => {
                        // Ignore refresh errors after successful save.
                    });
                    this.errors = {};
                    this.isSaving = false;
                    this.onUpdateActive = false;
                }).catch((err) => {
                    this.errors = err.response?.data?.errors || {};
                    this.isSaving = false;
                    this.onUpdateActive = false;
                });
            } catch (err) {
                this.isSaving = false;
                this.onUpdateActive = false;
                alertService.error(err);
            }
        },
    },
};
</script>
